import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useState } from 'react';
import { App as AntApp, Form, Input, Button, Typography, Space, Select } from 'antd';
import axios from 'axios';

import authStyles from '../styles/authShared.module.css';
import loginStyles from '../styles/login.module.css';
import ZibuzzLogo from '../assets/Ziniz-Icon-EPS.svg';
import { userManagementApi } from '../config/api';
import { Encrypt } from '../utils/encryption';
import { useAuth } from '../context/AuthContext';
import {
  PrefixFormItem,
  mobileForApi,
  isMobileLengthValid,
  MOBILE_MAX_LEN,
  mobileFieldInputPlain,
} from '../constants/CountryCodes';

const { Title } = Typography;

const Login = ({ onZibuzzSessionReady }) => {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [searchParams] = useSearchParams();
  const [hovered, setHovered] = useState(false);
  const [registerHovered, setRegisterHovered] = useState(false);
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    const userName = mobileForApi(values.prefix, values.mobile);
    setLoading(true);

    try {
      const responsefromLambda = await axios.post(
        userManagementApi,
        {
          ...Encrypt({
            userName,
            password: values.password,
            role: values.role ?? 'admin',
          }),
          operationType: 'login',
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = responsefromLambda.data;

      if (data.token) {
        setToken(data.token);
        message.success('Logged in successfully.');

        // Check if there's a redirect parameter
        const redirectTo = searchParams.get('redirect');

        if (typeof onZibuzzSessionReady === 'function') {
          onZibuzzSessionReady(data);
          // If redirect to subscription, navigate after session is ready
          if (redirectTo === 'subscription') {
            navigate('/?openSubscription=true', { replace: true });
          } else {
            navigate('/', { replace: true });
          }
          return;
        }

        // Handle redirect for non-callback scenarios
        if (redirectTo === 'subscription') {
          navigate('/?openSubscription=true', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        return;
      }

      const failureText =
        typeof data?.message === 'string' && data.message.trim()
          ? data.message
          : 'Login failed. Please check your credentials.';
      message.error(failureText);
    } catch (error) {
      console.error('Login error:', error);
      const errText =
        error.response?.data?.message ||
        'Something is not right. Please try again.';
      message.error(String(errText));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={authStyles.authPageShell}>
      <div className={loginStyles.loginContainer}>
        <div className={loginStyles.logoContainer}>
          <img
            src={ZibuzzLogo}
            alt="ZiBuzz Logo"
            className={loginStyles.logoImg}
          />
        </div>
        <Title level={2}>Login to ZiBuzz</Title>
        <Form
          form={form}
          name="login"
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          className={loginStyles.loginForm}
          initialValues={{ prefix: '+91', role: 'admin' }}
        >
          <Form.Item label="Mobile (WhatsApp number)" required>
            <div
              className={authStyles.emailInputWrapper}
              style={{ position: 'relative', width: '100%' }}
            >
              <Space.Compact style={{ width: '100%' }}>
                <PrefixFormItem />
                <Form.Item
                  name="mobile"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your mobile number',
                    },
                    {
                      validator: (_, value) => {
                        if (!value || !isMobileLengthValid(value)) {
                          return Promise.reject(new Error('Enter 6–14 digits'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <Input
                    data-testid="login-mobile-input"
                    className={loginStyles.mobileInput}
                    maxLength={MOBILE_MAX_LEN}
                    placeholder="Mobile number"
                    style={mobileFieldInputPlain}
                    onChange={(e) => {
                      const v = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, MOBILE_MAX_LEN);
                      form.setFieldsValue({ mobile: v });
                    }}
                  />
                </Form.Item>
              </Space.Compact>
            </div>
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            className={loginStyles.passwordField}
            rules={[{ required: true, message: 'Please enter your Password' }]}
          >
            <Input.Password data-testid="login-password-input" className={loginStyles.authControl} />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            className={loginStyles.roleField}
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select
              data-testid="login-role-select"
              className={`${loginStyles.authControl} ${loginStyles.roleSelectControl}`}
              options={[
                { value: 'admin', label: 'Admin' },
                { value: 'team_member', label: 'Team member' },
              ]}
            />
          </Form.Item>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <Link
              to="/register"
              onMouseEnter={() => setRegisterHovered(true)}
              onMouseLeave={() => setRegisterHovered(false)}
              style={{ fontSize: '0.9rem', transition: 'color 0.2s ease' }}
            >
              {registerHovered ? 'Register' : 'New user?'}
            </Link>
            <Link
              to="/resetpassword"
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              style={{ fontSize: '0.9rem', transition: 'color 0.2s ease' }}
            >
              {hovered ? 'Reset Password' : 'Forgot Password?'}
            </Link>
          </div>

          <Form.Item>
            <Button data-testid="login-submit-button" type="primary" htmlType="submit" block loading={loading}>
              {loading ? 'Validating...' : 'LOGIN'}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Login;
